// src/utils/applicationExport.ts

import jsPDF from 'jspdf';
import { format } from 'date-fns';

interface FormField {
  id: string;
  type: string;
  name: string;
  label: string;
  helperText?: string;
}

interface ApplicationData {
  _id: string;
  formType: string;
  formTitle: string;
  formSchema: {
    fields: FormField[];
  };
  values: Record<string, any>;
  rawValues: Record<string, any>;
  status: string;
  submittedAt: string;
  submittedBy: string | null;
}

const stripHtmlTags = (html: string): string => {
  const div = document.createElement('div');
  div.innerHTML = html;
  return div.textContent || div.innerText || '';
};

const formatValue = (value: any): string => {
  if (!value) return 'Not provided';
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

export const exportApplicationToPDF = (application: ApplicationData) => {
  const doc = new jsPDF();
  let yPos = 20;
  const margin = 20;
  const lineHeight = 7;
  const pageWidth = doc.internal.pageSize.width;
  const maxWidth = pageWidth - (margin * 2);

  // Add title
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text(application.formTitle, margin, yPos);
  yPos += lineHeight * 1.5;

  // Add applicant name
  const applicantName = application.values.firstName && application.values.lastName
    ? `${application.values.firstName} ${application.values.lastName}`
    : 'Applicant';
  doc.setFontSize(14);
  doc.setTextColor(33, 150, 243); // Primary blue color
  doc.text(applicantName, margin, yPos);
  doc.setTextColor(0, 0, 0); // Reset to black
  yPos += lineHeight * 2;

  // Add submission info
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`Submitted: ${format(new Date(application.submittedAt), 'PPpp')}`, margin, yPos);
  yPos += lineHeight;
  doc.text(`Status: ${application.status.toUpperCase()}`, margin, yPos);
  yPos += lineHeight * 2;

  // Add a line separator
  doc.setDrawColor(200, 200, 200);
  doc.line(margin, yPos, pageWidth - margin, yPos);
  yPos += lineHeight;

  // Add form fields
  application.formSchema.fields.forEach((field) => {
    const value = application.values[field.name];
    
    // Skip empty values
    if (!value || (Array.isArray(value) && value.length === 0)) {
      return;
    }

    // Check if we need a new page
    if (yPos > 270) {
      doc.addPage();
      yPos = 20;
    }

    // Add field label
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    const labelText = stripHtmlTags(field.label);
    doc.text(labelText, margin, yPos);
    yPos += lineHeight;

    // Add helper text if exists
    if (field.helperText) {
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(9);
      doc.setTextColor(100, 100, 100);
      const helperText = stripHtmlTags(field.helperText);
      const helperLines = doc.splitTextToSize(helperText, maxWidth);
      doc.text(helperLines, margin, yPos);
      yPos += lineHeight * helperLines.length;
      doc.setTextColor(0, 0, 0);
    }

    // Add field value
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    const valueText = formatValue(value);
    const valueLines = doc.splitTextToSize(valueText, maxWidth);
    doc.text(valueLines, margin, yPos);
    yPos += lineHeight * valueLines.length;
    yPos += lineHeight * 0.5; // Add space between fields

    // Add separator line
    if (yPos < 270) {
      doc.setDrawColor(230, 230, 230);
      doc.line(margin, yPos, pageWidth - margin, yPos);
      yPos += lineHeight;
    }
  });

  // Add footer with page numbers
  const pageCount = (doc as any).internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(150, 150, 150);
    doc.text(
      `Page ${i} of ${pageCount}`,
      pageWidth / 2,
      doc.internal.pageSize.height - 10,
      { align: 'center' }
    );
    doc.text(
      `Generated: ${format(new Date(), 'PP')}`,
      pageWidth - margin,
      doc.internal.pageSize.height - 10,
      { align: 'right' }
    );
  }

  // Save the PDF
  const fileName = `${applicantName.replace(/\s+/g, '_')}_${application.formType}_${format(new Date(), 'yyyy-MM-dd')}.pdf`;
  doc.save(fileName);
};

export const exportApplicationToCSV = (application: ApplicationData) => {
  // Prepare CSV headers
  const headers = ['Field', 'Value'];
  
  // Add metadata rows
  const applicantName = application.values.firstName && application.values.lastName
    ? `${application.values.firstName} ${application.values.lastName}`
    : 'Applicant';
  
  const rows: string[][] = [
    ['Form Title', application.formTitle],
    ['Applicant Name', applicantName],
    ['Submitted', format(new Date(application.submittedAt), 'PPpp')],
    ['Status', application.status.toUpperCase()],
    [''], // Empty row for separation
  ];

  // Add form field data
  application.formSchema.fields.forEach((field) => {
    const value = application.values[field.name];
    
    // Skip empty values
    if (!value || (Array.isArray(value) && value.length === 0)) {
      return;
    }

    const label = stripHtmlTags(field.label);
    const formattedValue = formatValue(value);
    rows.push([label, formattedValue]);
  });

  // Convert to CSV format
  const csvContent = [
    headers.join(','),
    ...rows.map(row => 
      row.map(cell => {
        // Escape quotes and wrap in quotes if contains comma, newline, or quote
        const cellStr = String(cell || '');
        if (cellStr.includes(',') || cellStr.includes('\n') || cellStr.includes('"')) {
          return `"${cellStr.replace(/"/g, '""')}"`;
        }
        return cellStr;
      }).join(',')
    )
  ].join('\n');

  // Create blob and download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  const fileName = `${applicantName.replace(/\s+/g, '_')}_${application.formType}_${format(new Date(), 'yyyy-MM-dd')}.csv`;
  
  link.setAttribute('href', url);
  link.setAttribute('download', fileName);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};